import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

type ActiveSpacePage = {
  // Space that SpaceChannel should subscribe to and join. Null for pages that
  // present a space over their own channel, such as shared spaces.
  channelSpaceName: string | null
}

const ActiveSpacePageContext = React.createContext<{
  activeSpacePage: ActiveSpacePage | null
  setActiveSpacePage: (page: ActiveSpacePage | null) => void
} | null>(null)

export default function ActiveSpacePageProvider({
  children,
}: PropsWithChildren) {
  const [activeSpacePage, setActiveSpacePage] =
    useState<ActiveSpacePage | null>(null)

  const value = useMemo(
    () => ({ activeSpacePage, setActiveSpacePage }),
    [activeSpacePage],
  )

  return (
    <ActiveSpacePageContext.Provider value={value}>
      {children}
    </ActiveSpacePageContext.Provider>
  )
}

export function useActiveSpacePage(): ActiveSpacePage | null {
  return useContext(ActiveSpacePageContext)?.activeSpacePage ?? null
}

// Pages that present a space announce themselves here so app-level components
// can step aside without having to recognise the page from its URL.
export function useDeclareActiveSpacePage({
  channelSpaceName,
}: ActiveSpacePage) {
  const setActiveSpacePage = useContext(
    ActiveSpacePageContext,
  )?.setActiveSpacePage

  useEffect(() => {
    if (!setActiveSpacePage) return

    setActiveSpacePage({ channelSpaceName })

    return () => setActiveSpacePage(null)
  }, [setActiveSpacePage, channelSpaceName])
}
